import { readFile } from "node:fs/promises"
import ts from "typescript"

import { collectFiles, componentKeyFrom, toRelative } from "./helpers.js"
import { ComponentDescriptor, ComponentUsageRecordsByPackage, UsageRecord, UsageSnippet } from "./types.js"

const sourcePattern = "**/*.{ts,tsx,js,jsx}"
const excludedFilePatterns = [".stories.", ".test.", ".spec."]
const minFallbackModuleScore = 3

type ImportBinding = {
  imported: string
  local: string
  moduleSpecifier: string
}

type PropUsageAccumulator = {
  count: number
  values: Map<string, number>
}

function escapeForRegex(input: string): string {
  return input.replaceAll(/[.*+?^${}()|[\]\\]/g, String.raw`\$&`)
}

function findImports(content: string, sourcePath: string): ImportBinding[] {
  const imports: ImportBinding[] = []
  const scriptKind = sourcePath.endsWith(".tsx")
    ? ts.ScriptKind.TSX
    : sourcePath.endsWith(".jsx")
      ? ts.ScriptKind.JSX
      : ts.ScriptKind.TS

  const sourceFile = ts.createSourceFile(sourcePath, content, ts.ScriptTarget.Latest, true, scriptKind)

  sourceFile.forEachChild(node => {
    if (!ts.isImportDeclaration(node) || !ts.isStringLiteral(node.moduleSpecifier)) {
      return
    }

    const { importClause } = node
    if (!importClause) {
      return
    }

    if (importClause.name) {
      imports.push({
        imported: "default",
        local: importClause.name.getText(sourceFile),
        moduleSpecifier: node.moduleSpecifier.text,
      })
    }

    if (importClause.namedBindings && ts.isNamedImports(importClause.namedBindings)) {
      for (const namedImport of importClause.namedBindings.elements) {
        const imported = namedImport.propertyName
          ? namedImport.propertyName.getText(sourceFile)
          : namedImport.name.getText(sourceFile)
        const local = namedImport.name.getText(sourceFile)
        imports.push({
          imported,
          local,
          moduleSpecifier: node.moduleSpecifier.text,
        })
      }
    }
  })

  return imports
}

function findImportedLocalNames(
  content: string,
  importFrom: string | string[],
  sourcePath: string,
): Array<{ imported: string; local: string; moduleSpecifier: string }> {
  const importFromSet = new Set(Array.isArray(importFrom) ? importFrom : [importFrom])

  const isImportFromMatch = (moduleSpecifier: string): boolean => {
    for (const packageName of importFromSet) {
      if (moduleSpecifier === packageName || moduleSpecifier.startsWith(`${packageName}/`)) {
        return true
      }
    }

    return false
  }

  return findImports(content, sourcePath)
    .filter(binding => isImportFromMatch(binding.moduleSpecifier))
    .map(binding => ({ imported: binding.imported, local: binding.local, moduleSpecifier: binding.moduleSpecifier }))
}

function resolveImportFromPackage(moduleSpecifier: string, importFromPackages: string[]): string | undefined {
  const normalizedPackages = [...new Set(importFromPackages.map(item => item.trim()).filter(Boolean))].sort(
    (a, b) => b.length - a.length || a.localeCompare(b),
  )

  for (const packageName of normalizedPackages) {
    if (moduleSpecifier === packageName || moduleSpecifier.startsWith(`${packageName}/`)) {
      return packageName
    }
  }

  return undefined
}

function lineAt(content: string, index: number): number {
  return content.slice(0, index).split("\n").length
}

function extractJsxBlocks(content: string, localName: string, sourcePath: string): UsageSnippet[] {
  const escapedName = escapeForRegex(localName)
  const tagPattern = new RegExp(`<\\/?${escapedName}\\b[^>]*>`, "g")
  const tags = [...content.matchAll(tagPattern)]
  const snippets: UsageSnippet[] = []

  for (let i = 0; i < tags.length; i++) {
    const tag = tags[i]
    const tagText = tag[0]
    const startIndex = tag.index ?? 0

    if (tagText.startsWith("</")) {
      continue
    }

    if (tagText.endsWith("/>")) {
      const endIndex = startIndex + tagText.length
      snippets.push({
        lineEnd: lineAt(content, endIndex),
        lineStart: lineAt(content, startIndex),
        snippet: content.slice(startIndex, endIndex).trim(),
        sourcePath,
      })
      continue
    }

    let depth = 1
    let endIndex = startIndex + tagText.length

    for (let j = i + 1; j < tags.length; j++) {
      const innerTag = tags[j]
      const innerText = innerTag[0]
      const innerIndex = innerTag.index ?? 0

      if (innerText.startsWith("</")) {
        depth -= 1
        if (depth === 0) {
          endIndex = innerIndex + innerText.length
          break
        }

        continue
      }

      if (!innerText.endsWith("/>")) {
        depth += 1
      }
    }

    snippets.push({
      lineEnd: lineAt(content, endIndex),
      lineStart: lineAt(content, startIndex),
      snippet: content.slice(startIndex, endIndex).trim(),
      sourcePath,
    })
  }

  return snippets
}

function extractCallUsages(content: string, localName: string, sourcePath: string): UsageSnippet[] {
  const callPattern = new RegExp(`${escapeForRegex(localName)}\\s*\\(`, "g")
  const snippets: UsageSnippet[] = []

  for (const match of content.matchAll(callPattern)) {
    const index = match.index ?? 0
    const line = lineAt(content, index)
    const lineText = content.split("\n")[line - 1] ?? ""

    snippets.push({
      lineEnd: line,
      lineStart: line,
      snippet: lineText.trim(),
      sourcePath,
    })
  }

  return snippets
}

function normalizeValueText(input: string, maxLength = 120): string {
  const normalized = input.replaceAll(/\s+/g, " ").trim()
  if (normalized.length <= maxLength) {
    return normalized
  }

  return `${normalized.slice(0, maxLength - 1)}…`
}

function extractJsxPropUsage(
  content: string,
  localName: string,
  sourcePath: string,
): Map<string, PropUsageAccumulator> {
  const propUsage = new Map<string, PropUsageAccumulator>()
  const scriptKind = sourcePath.endsWith(".tsx")
    ? ts.ScriptKind.TSX
    : sourcePath.endsWith(".jsx")
      ? ts.ScriptKind.JSX
      : ts.ScriptKind.TS
  const sourceFile = ts.createSourceFile(sourcePath, content, ts.ScriptTarget.Latest, true, scriptKind)

  const getTagName = (name: ts.JsxTagNameExpression): string | null => {
    if (ts.isIdentifier(name)) {
      return name.text
    }

    if (ts.isPropertyAccessExpression(name)) {
      return name.name.text
    }

    return null
  }

  const readJsxAttributeValue = (attribute: ts.JsxAttribute): string => {
    if (!attribute.initializer) {
      return "{true}"
    }

    if (ts.isStringLiteral(attribute.initializer)) {
      return `"${normalizeValueText(attribute.initializer.text)}"`
    }

    if (ts.isJsxExpression(attribute.initializer)) {
      if (!attribute.initializer.expression) {
        return "{}"
      }

      const { expression } = attribute.initializer
      if (
        ts.isStringLiteralLike(expression) ||
        ts.isNumericLiteral(expression) ||
        expression.kind === ts.SyntaxKind.TrueKeyword ||
        expression.kind === ts.SyntaxKind.FalseKeyword ||
        expression.kind === ts.SyntaxKind.NullKeyword
      ) {
        return normalizeValueText(expression.getText(sourceFile))
      }

      return `{${normalizeValueText(expression.getText(sourceFile))}}`
    }

    return normalizeValueText(attribute.initializer.getText(sourceFile))
  }

  const addPropUsage = (propName: string, value: string): void => {
    const existing = propUsage.get(propName) ?? { count: 0, values: new Map<string, number>() }
    existing.count += 1
    existing.values.set(value, (existing.values.get(value) ?? 0) + 1)
    propUsage.set(propName, existing)
  }

  const inspectOpeningLikeElement = (node: ts.JsxOpeningLikeElement): void => {
    const tagName = getTagName(node.tagName)
    if (tagName !== localName) {
      return
    }

    for (const attribute of node.attributes.properties) {
      if (ts.isJsxSpreadAttribute(attribute)) {
        continue
      }

      const propName = attribute.name.getText(sourceFile)
      const propValue = readJsxAttributeValue(attribute)
      addPropUsage(propName, propValue)
    }
  }

  const visit = (node: ts.Node): void => {
    if (ts.isJsxSelfClosingElement(node)) {
      inspectOpeningLikeElement(node)
    } else if (ts.isJsxOpeningElement(node)) {
      inspectOpeningLikeElement(node)
    }

    ts.forEachChild(node, visit)
  }

  visit(sourceFile)
  return propUsage
}

function mergePropUsage(
  target: Record<string, PropUsageAccumulator> | undefined,
  incoming: Map<string, PropUsageAccumulator>,
): Record<string, PropUsageAccumulator> {
  const out = target ?? {}

  for (const [propName, usage] of incoming.entries()) {
    const existing = out[propName] ?? { count: 0, values: new Map<string, number>() }
    existing.count += usage.count
    for (const [value, valueCount] of usage.values.entries()) {
      existing.values.set(value, (existing.values.get(value) ?? 0) + valueCount)
    }

    out[propName] = existing
  }

  return out
}

export async function analyzeComponentUsage(
  projectRoot: string,
  components: ComponentDescriptor[],
  importFrom: string | string[],
  excludePaths: string[] = [],
): Promise<{ components: ComponentDescriptor[]; records: ComponentUsageRecordsByPackage }> {
  const files = await collectFiles({
    excludeNestedPackages: true,
    excludePaths,
    pattern: sourcePattern,
    projectRoot,
  })
  const recordsByPackage: ComponentUsageRecordsByPackage = {}
  const componentByExportName = new Map(components.map(component => [component.exportName, component]))
  const inferredComponentsByExportName = new Map<string, ComponentDescriptor>()
  const inferredKeys = new Set<string>()
  const fallbackModuleScores = new Map<string, number>()

  const resolveComponent = (
    imported: string,
    local: string,
    moduleSpecifier: string,
  ): ComponentDescriptor | undefined => {
    const known = componentByExportName.get(imported)
    if (known) {
      return known
    }

    const inferredName = imported === "default" ? local : imported
    if (!inferredName || inferredName === "default") {
      return undefined
    }

    const existing = inferredComponentsByExportName.get(inferredName)
    if (existing) {
      return existing
    }

    const descriptor: ComponentDescriptor = {
      componentKey: componentKeyFrom(inferredName, `${moduleSpecifier}:${inferredName}`, inferredKeys),
      componentPath: moduleSpecifier,
      exportName: inferredName,
    }
    inferredComponentsByExportName.set(inferredName, descriptor)
    return descriptor
  }

  const runScan = async (targetImportFrom: string | string[]): Promise<void> => {
    const targetImportFromList = Array.isArray(targetImportFrom) ? targetImportFrom : [targetImportFrom]
    for (const file of files) {
      if (excludedFilePatterns.some(pattern => file.includes(pattern))) {
        continue
      }

      const relativePath = toRelative(projectRoot, file)
      const content = await readFile(file, "utf8")

      // Gather potential fallback module names based on import overlap with known DS components.
      for (const binding of findImports(content, relativePath)) {
        if (binding.moduleSpecifier.startsWith(".")) {
          continue
        }

        if (!componentByExportName.has(binding.imported)) {
          continue
        }

        fallbackModuleScores.set(binding.moduleSpecifier, (fallbackModuleScores.get(binding.moduleSpecifier) ?? 0) + 1)
      }

      const importBindings = findImportedLocalNames(content, targetImportFromList, relativePath)
      if (importBindings.length === 0) {
        continue
      }

      for (const importBinding of importBindings) {
        const importFromPackage = resolveImportFromPackage(importBinding.moduleSpecifier, targetImportFromList)
        if (!importFromPackage) {
          continue
        }

        const component = resolveComponent(importBinding.imported, importBinding.local, importBinding.moduleSpecifier)
        if (!component) {
          continue
        }

        const jsxSnippets = extractJsxBlocks(content, importBinding.local, relativePath)
        const snippets =
          jsxSnippets.length > 0 ? jsxSnippets : extractCallUsages(content, importBinding.local, relativePath)
        const propUsage = extractJsxPropUsage(content, importBinding.local, relativePath)

        if (snippets.length === 0) {
          continue
        }

        const packageRecords = recordsByPackage[importFromPackage] ?? {}
        const existing = packageRecords[component.componentKey] ?? {
          count: 0,
          files: [],
          propUsage: {},
          snippets: [],
        }

        existing.count += snippets.length
        existing.files.push(relativePath)
        existing.snippets.push(...snippets)
        existing.propUsage = mergePropUsage(
          existing.propUsage as Record<string, PropUsageAccumulator> | undefined,
          propUsage,
        ) as unknown as UsageRecord["propUsage"]
        packageRecords[component.componentKey] = existing
        recordsByPackage[importFromPackage] = packageRecords
      }
    }
  }

  const importFromList = Array.isArray(importFrom) ? importFrom : [importFrom]
  await runScan(importFromList)

  const hasUsage = Object.values(recordsByPackage).some(packageRecords =>
    Object.values(packageRecords).some(record => record.count > 0),
  )
  if (!hasUsage) {
    const bestFallback = [...fallbackModuleScores.entries()]
      .filter(([moduleName, score]) => !importFromList.includes(moduleName) && score >= minFallbackModuleScore)
      .sort((a, b) => b[1] - a[1])[0]

    if (bestFallback) {
      await runScan(bestFallback[0])
    }
  }

  for (const packageRecords of Object.values(recordsByPackage)) {
    for (const componentKey of Object.keys(packageRecords)) {
      packageRecords[componentKey].files = [...new Set(packageRecords[componentKey].files)]
        .sort((a, b) => a.localeCompare(b))
        .slice(0, 30)
      packageRecords[componentKey].snippets = packageRecords[componentKey].snippets.sort((a, b) => {
        if (a.sourcePath === b.sourcePath) {
          return a.lineStart - b.lineStart
        }

        return a.sourcePath.localeCompare(b.sourcePath)
      })

      const currentPropUsage = packageRecords[componentKey].propUsage as
        | Record<string, PropUsageAccumulator>
        | undefined
      if (currentPropUsage) {
        packageRecords[componentKey].propUsage = Object.fromEntries(
          Object.entries(currentPropUsage)
            .sort((a, b) => b[1].count - a[1].count || a[0].localeCompare(b[0]))
            .map(([propName, usage]) => [
              propName,
              {
                count: usage.count,
                values: [...usage.values.entries()]
                  .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
                  .map(([value, count]) => ({ count, value })),
              },
            ]),
        )
      }
    }
  }

  const resolvedComponents = components.length > 0 ? components : [...inferredComponentsByExportName.values()]

  return {
    components: resolvedComponents.sort((a, b) => a.componentKey.localeCompare(b.componentKey)),
    records: recordsByPackage,
  }
}
